/**
 * Set or change the account password, in app.
 *
 * Two modes resolved from `checkHasPassword()`:
 *  - `change`: the account has a credential row -- current + new + confirm.
 *  - `set`:    a social-only account adding its first password -- new + confirm.
 *
 * Error routing branches on status codes and error codes only, never on message
 * text (the reset-password screen learned that the hard way: a server copy
 * change silently rerouted the UI). An unusable current password is the one
 * failure attributable to a field; everything else is a toast.
 */

import { ScreenHeader } from '@/components/layout/screen-header';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { TextInput } from '@/components/ui/text-input';
import { toast } from '@/components/ui/toast';
import { PASSWORD_ALREADY_SET, useAuth } from '@/hooks/use-auth';
import { validatePassword } from '@/utils/auth-validation';
import { createLogger } from '@/utils/logger';
import { router } from 'expo-router';
import { useEffect, useRef, useState } from 'react';
import { ScrollView, View, type TextInput as RNTextInput } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

const logger = createLogger('PASSWORD_SCREEN');

/** Which form the screen shows. `null` while `checkHasPassword` resolves. */
type PasswordMode = 'change' | 'set' | null;

type AuthError = { message?: string; status?: number; code?: string };

const COPY = {
  screenTitle: 'Password',
  changeHeading: 'Change your password',
  setHeading: 'Set a password',
  changeIntro:
    'Choose a new password. You will stay signed in here, and be signed out everywhere else.',
  setIntro:
    'You signed in with a social provider, so this account has no password yet. Adding one gives you a second way in -- and lets you disconnect a provider later.',
  currentLabel: 'Current password',
  newLabel: 'New password',
  confirmLabel: 'Confirm new password',
  submit: 'Save password',
  changeSubmit: 'Update password',
  setSubmit: 'Set password',
  currentRequired: 'Current password is required',
  currentIncorrect: 'Current password is incorrect',
  confirmRequired: 'Please confirm your password',
  confirmMismatch: 'Passwords do not match',
  alreadySetNotice:
    'You already have a password on this account. Enter it below to change it instead.',
  changeSuccess: 'Password updated',
  setSuccess: 'Password set',
  successDescription: 'You have been signed out on your other devices.',
  failureTitle: 'Could not save your password',
  failureDescription: 'Something went wrong on our end. Try again in a moment.',
} as const;

const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_CONFLICT = 409;
const INVALID_PASSWORD_CODE = 'INVALID_PASSWORD';

/**
 * Whether a change-password failure is attributable to the current password
 * the user typed. Status/code only: `changePassword` answers a rejected
 * credential with a 400 (`INVALID_PASSWORD`), and a 401 means the same thing
 * from the session's point of view.
 */
function isRejectedCurrentPassword(error: AuthError): boolean {
  if (error.code === INVALID_PASSWORD_CODE) return true;
  return error.status === HTTP_BAD_REQUEST || error.status === HTTP_UNAUTHORIZED;
}

/** The account gained a password between the mode check and the submit. */
function isPasswordAlreadySet(error: AuthError): boolean {
  return error.status === HTTP_CONFLICT || error.code === PASSWORD_ALREADY_SET;
}

export function PasswordScreen() {
  const { checkHasPassword, changePassword, setPassword } = useAuth();

  const [mode, setMode] = useState<PasswordMode>(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);

  const newRef = useRef<RNTextInput>(null);
  const confirmRef = useRef<RNTextInput>(null);

  useEffect(() => {
    let cancelled = false;
    checkHasPassword()
      .then((hasPassword) => {
        if (!cancelled) setMode(hasPassword ? 'change' : 'set');
      })
      .catch(() => {
        // Fail safe: assume a password exists so we never offer to "set" one
        // over the top of a password we simply failed to see.
        if (!cancelled) setMode('change');
      });
    return () => {
      cancelled = true;
    };
  }, [checkHasPassword]);

  const clearError = (field: string) =>
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));

  const validateInputs = (): boolean => {
    const next: Record<string, string> = {};

    if (mode === 'change' && !currentPassword) {
      next.currentPassword = COPY.currentRequired;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) next.newPassword = passwordError;

    if (!confirmPassword) {
      next.confirmPassword = COPY.confirmRequired;
    } else if (newPassword !== confirmPassword) {
      next.confirmPassword = COPY.confirmMismatch;
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleFailure = (error: AuthError) => {
    logger.error('Password save failed:', error);

    if (mode === 'change' && isRejectedCurrentPassword(error)) {
      setErrors({ currentPassword: COPY.currentIncorrect });
      return;
    }

    if (mode === 'set' && isPasswordAlreadySet(error)) {
      setMode('change');
      setNotice(COPY.alreadySetNotice);
      return;
    }

    toast.error(COPY.failureTitle, {
      description: error.message || COPY.failureDescription,
    });
  };

  const handleSuccess = () => {
    toast.success(mode === 'change' ? COPY.changeSuccess : COPY.setSuccess, {
      description: COPY.successDescription,
    });
    router.back();
  };

  const handleSubmit = async () => {
    if (mode === null || isLoading) return;
    if (!validateInputs()) return;

    setIsLoading(true);
    setNotice(null);

    try {
      const result =
        mode === 'change'
          ? await changePassword(currentPassword, newPassword)
          : await setPassword(newPassword);

      if (result.error) {
        handleFailure(result.error);
        return;
      }
      handleSuccess();
    } catch (error) {
      logger.error('Password save threw:', error);
      toast.error(COPY.failureTitle, { description: COPY.failureDescription });
    } finally {
      setIsLoading(false);
    }
  };

  const isSetMode = mode === 'set';

  return (
    <SafeAreaView className="flex-1" edges={['top']}>
      <ScreenHeader title={COPY.screenTitle} showBackButton />
      <ScrollView
        className="flex-1"
        showsVerticalScrollIndicator={false}
        contentContainerClassName="px-4 pb-8"
      >
        <Text className="text-2xl font-bold mt-2 mb-2 text-foreground">
          {isSetMode ? COPY.setHeading : COPY.changeHeading}
        </Text>
        <Text className="text-base mb-6 text-muted-foreground">
          {isSetMode ? COPY.setIntro : COPY.changeIntro}
        </Text>

        {notice ? (
          <Text
            className="text-sm mb-4 text-foreground"
            accessibilityLiveRegion="polite"
          >
            {notice}
          </Text>
        ) : null}

        <View className="gap-4">
          {mode === 'change' ? (
            <TextInput
              label={COPY.currentLabel}
              placeholder="Enter your current password"
              value={currentPassword}
              onChangeText={(text) => {
                setCurrentPassword(text);
                clearError('currentPassword');
              }}
              secureTextEntry={!showCurrent}
              autoCapitalize="none"
              autoCorrect={false}
              spellCheck={false}
              autoComplete="current-password"
              textContentType="password"
              returnKeyType="next"
              onSubmitEditing={() => newRef.current?.focus()}
              submitBehavior="submit"
              editable={!isLoading}
              error={errors.currentPassword || undefined}
              rightIcon={
                <Button
                  variant="link"
                  size="sm"
                  onPress={() => setShowCurrent(!showCurrent)}
                  accessibilityLabel={
                    showCurrent ? 'Hide current password' : 'Show current password'
                  }
                  testID="password-toggle-current"
                >
                  {showCurrent ? 'Hide' : 'Show'}
                </Button>
              }
            />
          ) : null}

          <TextInput
            ref={newRef}
            label={COPY.newLabel}
            placeholder="At least 8 characters"
            value={newPassword}
            onChangeText={(text) => {
              setNewPassword(text);
              clearError('newPassword');
            }}
            secureTextEntry={!showNew}
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            autoComplete="new-password"
            textContentType="newPassword"
            returnKeyType="next"
            onSubmitEditing={() => confirmRef.current?.focus()}
            submitBehavior="submit"
            editable={!isLoading}
            error={errors.newPassword || undefined}
            rightIcon={
              <Button
                variant="link"
                size="sm"
                onPress={() => setShowNew(!showNew)}
                accessibilityLabel={showNew ? 'Hide new password' : 'Show new password'}
                testID="password-toggle-new"
              >
                {showNew ? 'Hide' : 'Show'}
              </Button>
            }
          />

          <TextInput
            ref={confirmRef}
            label={COPY.confirmLabel}
            placeholder="Re-enter your new password"
            value={confirmPassword}
            onChangeText={(text) => {
              setConfirmPassword(text);
              clearError('confirmPassword');
            }}
            secureTextEntry={!showConfirm}
            autoCapitalize="none"
            autoCorrect={false}
            spellCheck={false}
            autoComplete="new-password"
            textContentType="newPassword"
            returnKeyType="go"
            onSubmitEditing={handleSubmit}
            editable={!isLoading}
            error={errors.confirmPassword || undefined}
            rightIcon={
              <Button
                variant="link"
                size="sm"
                onPress={() => setShowConfirm(!showConfirm)}
                accessibilityLabel={
                  showConfirm ? 'Hide confirmed password' : 'Show confirmed password'
                }
                testID="password-toggle-confirm"
              >
                {showConfirm ? 'Hide' : 'Show'}
              </Button>
            }
          />

          <Button
            size="lg"
            fullWidth
            className="mt-2"
            onPress={handleSubmit}
            disabled={mode === null}
            loading={isLoading}
            accessibilityLabel={COPY.submit}
            testID="password-submit"
          >
            {isSetMode ? COPY.setSubmit : COPY.changeSubmit}
          </Button>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}
